import React, { useEffect, useImperativeHandle, useState, forwardRef } from "react"
import { Row, Col } from "reactstrap"
import { Link } from "react-router-dom"
import axios from "axios"
import { saveAs } from "file-saver"
import { toast } from "react-toastify"
import { getCurrentUser, getLeadTagName } from "../../helpers/Utils"
import Loader from "../Loader/loader.js"

const TAG0_ID = "tag0"
const TAG0_COLUMN = { id: TAG0_ID, name: "Tag0" }
const CUSTOM_REPORT_CACHE_KEY = "CustomReportData"
const CUSTOM_REPORT_CACHE_TTL = 30 * 60 * 1000

let customReportMemory = {
  filterKey: "",
  savedAt: 0,
  tags: [],
  users: [],
  sources: [],
  leads: [],
}

const getReportFilterKey = (fromDate, toDate, leadholderId) =>
  [
    localStorage.getItem("company_name") || "",
    fromDate || "",
    toDate || "",
    leadholderId || "",
  ].join("|")

const isFreshCache = savedAt =>
  savedAt && Date.now() - Number(savedAt) < CUSTOM_REPORT_CACHE_TTL

const readDiskReportCache = filterKey => {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(CUSTOM_REPORT_CACHE_KEY) || "null"
    )
    if (
      !parsed ||
      parsed.filterKey !== filterKey ||
      !isFreshCache(parsed.savedAt)
    ) {
      return null
    }
    return {
      tags: parsed.tags || [],
      users: parsed.users || [],
      sources: parsed.sources || [],
      leads: [],
    }
  } catch (error) {
    return null
  }
}

const writeReportCache = (filterKey, report) => {
  customReportMemory = {
    filterKey,
    savedAt: Date.now(),
    tags: report.tags || [],
    users: report.users || [],
    sources: report.sources || [],
    leads: report.leads || [],
  }
  try {
    localStorage.setItem(
      CUSTOM_REPORT_CACHE_KEY,
      JSON.stringify({
        filterKey,
        savedAt: customReportMemory.savedAt,
        tags: customReportMemory.tags,
        users: customReportMemory.users,
        sources: customReportMemory.sources,
      })
    )
  } catch (error) {
    // Ignore quota errors; memory cache still keeps the tab fast.
  }
}

const readMemoryReportCache = filterKey => {
  if (
    customReportMemory.filterKey !== filterKey ||
    !isFreshCache(customReportMemory.savedAt)
  ) {
    return null
  }
  return {
    tags: customReportMemory.tags,
    users: customReportMemory.users,
    sources: customReportMemory.sources,
    leads: customReportMemory.leads || [],
  }
}

const getAuthHeaders = () => ({
  headers: {
    "content-type": "application/json",
    Authorization: `Bearer ${getCurrentUser()?.token || ""}`,
  },
})

const safeJson = (key, fallback = []) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "null") || fallback
  } catch (error) {
    return fallback
  }
}

const hasLeadTag = tagId =>
  tagId !== null && tagId !== undefined && tagId !== "" && Number(tagId) !== 0

const csvEscape = value => {
  const text = value === null || value === undefined ? "" : String(value)
  return `"${text.replace(/"/g, '""')}"`
}

const formatLeadStatus = type => {
  if (Array.isArray(type)) return type.filter(Boolean).join(", ")
  return type || ""
}

const getLeadRequirement = lead => {
  const firstListed =
    (Array.isArray(lead.messages) &&
      (lead.messages[0]?.message || lead.messages[0]?.notes || lead.messages[0])) ||
    (Array.isArray(lead.followups) &&
      [...lead.followups].sort((a, b) => Number(a.id || 0) - Number(b.id || 0))[0]
        ?.message) ||
    ""
  return (
    lead.message ||
    lead.first_message ||
    lead.requirement ||
    firstListed ||
    lead.notes ||
    lead.last_message ||
    ""
  )
}

const getLeadTagLabel = lead => {
  if (hasLeadTag(lead.tag_id)) {
    return lead.tag_name || getLeadTagName(lead.tag_id) || ""
  }
  return TAG0_COLUMN.name
}

const getLeadSourceName = lead =>
  lead.lead_source || lead.lead_source_name || ""

const EXPORT_COLUMNS = [
  { header: "Lead Name", value: lead => lead.name || "" },
  { header: "Contact Number", value: lead => lead.number || "" },
  { header: "Date Received", value: lead => lead.created_at || lead.date || "" },
  {
    header: "Assigned User",
    value: lead => lead.leadholder_name || lead.leadholder || "",
  },
  { header: "Lead Source", value: getLeadSourceName },
  { header: "Requirement", value: getLeadRequirement },
  { header: "Lead Status", value: lead => formatLeadStatus(lead.type) },
  {
    header: "Follow-up Date",
    value: lead => lead.next_followup_date || lead.followup_date || "",
  },
  {
    header: "Remarks",
    value: lead => lead.remarks || lead.last_message || "",
  },
  { header: "Lead Tag", value: getLeadTagLabel },
  { header: "Lead Source", value: getLeadSourceName },
  { header: "State", value: lead => lead.state || "" },
  { header: "City", value: lead => lead.city || "" },
  { header: "Distt", value: lead => lead.district || "" },
]

const CustomReport = forwardRef(({ fromDate, toDate, leadholderId, refreshKey }, ref) => {
  const [loader, setLoader] = useState("")
  const [error, setError] = useState("")
  const [tags, setTags] = useState([])
  const [users, setUsers] = useState([])
  const [sources, setSources] = useState([])
  const [exportLeads, setExportLeads] = useState([])

  const dateQuery = () => {
    const params = []
    if (fromDate) params.push(`from_date=${fromDate}`)
    if (toDate) params.push(`to_date=${toDate}`)
    if (leadholderId) params.push(`leadholder_id=${leadholderId}`)
    return params
  }

  const leadListQuery = extra => {
    const params = dateQuery()
    Object.entries(extra || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        params.push(`${key}=${value}`)
      }
    })
    return params.length ? `?${params.join("&")}` : ""
  }

  const fetchLeadsPage = async page => {
    const company = localStorage.getItem("company_name")
    const params = dateQuery()
    params.push("per_page=1000", `page=${page}`)
    const res = await axios.get(
      `${process.env.REACT_APP_API_URL}/${company}/lead?${params.join("&")}`,
      getAuthHeaders()
    )
    return res.data
  }

  const buildReportFromLeads = allLeads => {
    const tagMap = {}
    const userMap = {}
    const sourceMap = {}

    allLeads.forEach(lead => {
      const userId = lead.leadholder_id || "unassigned"
      const userName = lead.leadholder_id
        ? lead.leadholder_name || lead.leadholder || "Unassigned"
        : "Unassigned"
      const tagId = lead.tag_id
      const tagName = lead.tag_name
      const sourceId = lead.lead_source_id
      const sourceName =
        lead.lead_source || lead.lead_source_name || "Unknown"

      if (hasLeadTag(tagId)) {
        tagMap[tagId] = tagName || `Tag ${tagId}`
      }

      if (!userMap[userId]) {
        userMap[userId] = { id: userId, name: userName, counts: {} }
      }
      const key = hasLeadTag(tagId) ? String(tagId) : TAG0_ID
      userMap[userId].counts[key] = (userMap[userId].counts[key] || 0) + 1

      if (sourceId) {
        if (!sourceMap[sourceId]) {
          sourceMap[sourceId] = {
            id: sourceId,
            name: sourceName,
            count: 0,
          }
        }
        sourceMap[sourceId].count += 1
      }
    })

    const storedTags = safeJson("AllTags")
    const reportTags = [
      TAG0_COLUMN,
      ...(storedTags.length
        ? storedTags.map(tag => ({ id: tag.id, name: tag.name }))
        : Object.entries(tagMap).map(([id, name]) => ({ id, name }))),
    ]

    const storedUsers = safeJson("AllUsers")
    const reportUsers =
      storedUsers.length && !leadholderId
        ? [
            ...storedUsers.map(user => ({
              id: user.id,
              name: user.name,
              counts: userMap[user.id]?.counts || {},
            })),
            ...(userMap.unassigned ? [userMap.unassigned] : []),
          ]
        : Object.values(userMap)

    const storedSources = safeJson("AllSource")
    const reportSources = storedSources.length
      ? storedSources.map(source => ({
          id: source.id,
          name: source.name,
          count: sourceMap[source.id]?.count || 0,
        }))
      : Object.values(sourceMap)

    return {
      tags: reportTags,
      users: reportUsers,
      sources: reportSources.filter(
        source => source.count > 0 || storedSources.length
      ),
      leads: allLeads,
    }
  }

  const aggregateFromLeads = async () => {
    const first = await fetchLeadsPage(1)
    if (!first?.status) {
      throw new Error(first?.message || "Failed to load custom report")
    }
    const allLeads = [...(first.data?.data || [])]
    const lastPage = Math.min(first.data?.last_page || 1, 4)
    if (lastPage > 1) {
      const rest = await Promise.all(
        Array.from({ length: lastPage - 1 }, (_, index) =>
          fetchLeadsPage(index + 2)
        )
      )
      rest.forEach(payload => {
        if (payload?.status) {
          allLeads.push(...(payload.data?.data || []))
        }
      })
    }
    return buildReportFromLeads(allLeads)
  }

  const applyAggregatedReport = report => {
    setTags(report.tags)
    setUsers(report.users)
    setSources(report.sources)
    setExportLeads(report.leads || [])
    setLoader("")
  }

  const getCustomReport = () => {
    setError("")
    const filterKey = getReportFilterKey(fromDate, toDate, leadholderId)
    const memoryReport = readMemoryReportCache(filterKey)
    if (memoryReport) {
      applyAggregatedReport(memoryReport)
      return
    }

    const diskReport = readDiskReportCache(filterKey)
    if (diskReport) {
      applyAggregatedReport(diskReport)
      aggregateFromLeads()
        .then(report => {
          writeReportCache(filterKey, report)
          applyAggregatedReport(report)
        })
        .catch(() => {})
      return
    }

    setLoader(<Loader />)
    aggregateFromLeads()
      .then(report => {
        writeReportCache(filterKey, report)
        applyAggregatedReport(report)
      })
      .catch(() => {
        setError("Failed to load custom report")
        setLoader("")
      })
  }

  const exportLeadsCsv = async () => {
    let rows = exportLeads
    if (!rows.length) {
      try {
        const report = await aggregateFromLeads()
        const filterKey = getReportFilterKey(fromDate, toDate, leadholderId)
        writeReportCache(filterKey, report)
        applyAggregatedReport(report)
        rows = report.leads || []
      } catch (error) {
        toast.error("No leads available to export", {
          position: "top-center",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        })
        return
      }
    }
    if (!rows.length) {
      toast.error("No leads available to export", {
        position: "top-center",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      })
      return
    }
    const header = EXPORT_COLUMNS.map(column => csvEscape(column.header)).join(
      ","
    )
    const csvRows = rows.map(lead =>
      EXPORT_COLUMNS.map(column => csvEscape(column.value(lead))).join(",")
    )
    const csv = `\uFEFF${[header, ...csvRows].join("\n")}`
    const stamp = fromDate || toDate ? `-${fromDate || "start"}-${toDate || "end"}` : ""
    saveAs(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
      `custom-report-leads${stamp}.csv`
    )
  }

  useImperativeHandle(ref, () => ({
    exportLeads: exportLeadsCsv,
  }))

  const tagCountQuery = (user, tag) => {
    const extra = { leadholder_id: user.id === "unassigned" ? "" : user.id }
    if (String(tag.id) !== TAG0_ID) {
      extra.tag_id = tag.id
    }
    return `/leads${leadListQuery(extra)}`
  }

  useEffect(() => {
    getCustomReport()
  }, [fromDate, toDate, leadholderId, refreshKey])

  const getUserTagCount = (user, tagId) =>
    Number(user.counts?.[String(tagId)] || 0)

  const tagTotals = tags.map(tag =>
    users.reduce((sum, user) => sum + getUserTagCount(user, tag.id), 0)
  )
  const visibleSources = [...sources]
    .filter(source => Number(source.count) > 0)
    .sort((a, b) => Number(b.count) - Number(a.count))
  const sourceTotal = visibleSources.reduce(
    (sum, source) => sum + (source.count || 0),
    0
  )

  const renderCount = (count, to) => {
    if (!count) {
      return <span className="custom-report-zero">0</span>
    }
    return (
      <Link to={to} className="custom-report-count">
        {count}
      </Link>
    )
  }

  return (
    <>
      <style>{`
        .custom-report-wrap { align-items: stretch; }
        .custom-report-table {
          margin-bottom: 0;
          border-collapse: separate;
          border-spacing: 0;
          border: 1px solid #e6ebf1;
          border-radius: 8px;
          width: max-content;
          min-width: 100%;
        }
        .custom-report-table thead th {
          background: #50a5a6;
          color: #fff;
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.3px;
          text-transform: uppercase;
          border: none;
          padding: 10px 12px;
          white-space: nowrap;
        }
        .custom-report-table tbody td {
          font-size: 13px;
          padding: 8px 12px;
          border-color: #eef2f6;
          vertical-align: middle;
        }
        .custom-report-table tbody tr:hover td {
          background: #f7fbfb;
        }
        .custom-report-table .custom-report-total td {
          background: #f3f6f9;
          font-weight: 700;
          color: #2b3a4a;
        }
        .custom-report-count {
          display: inline-flex;
          min-width: 28px;
          height: 24px;
          align-items: center;
          justify-content: center;
          padding: 0 8px;
          border-radius: 12px;
          background: #e8f6f6;
          color: #2d7a7b;
          font-weight: 700;
          text-decoration: none;
        }
        .custom-report-count:hover {
          background: #50a5a6;
          color: #fff;
        }
        .custom-report-zero {
          color: #b7c0cc;
        }
        .custom-report-user {
          color: #2d7a7b;
          font-weight: 600;
        }
        .custom-report-user:hover {
          color: #1f5d5e;
        }
        .custom-report-scroll {
          max-height: 420px;
          overflow: auto;
          border-radius: 8px;
        }
        .custom-report-table th:first-child,
        .custom-report-table td:first-child {
          position: sticky;
          left: 0;
          z-index: 2;
          min-width: 150px;
        }
        .custom-report-table thead th:first-child {
          z-index: 3;
          background: #50a5a6;
        }
        .custom-report-table tbody td:first-child {
          background: #fff;
        }
        .custom-report-table tbody tr:hover td:first-child {
          background: #f7fbfb;
        }
        .custom-report-table .custom-report-total td:first-child {
          background: #f3f6f9;
        }
        .custom-report-table th:not(:first-child),
        .custom-report-table td:not(:first-child) {
          min-width: 88px;
        }
      `}</style>
      <Row className="custom-report-wrap g-3">
        <Col lg={8} md={12}>
          <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: "12px" }}>
            User Tag Report
          </h6>
          <div className="custom-report-scroll table-responsive">
            <table className="table table-centered table-nowrap custom-report-table">
              <thead>
                <tr>
                  <th className="text-start">User</th>
                  {tags.map(tag => (
                    <th key={tag.id} className="text-center">
                      {tag.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.length ? (
                  users.map(user => (
                    <tr key={user.id}>
                      <td className="text-start">
                        <Link
                          className="custom-report-user"
                          to={`/leads?leadholder_id=${user.id}`}
                        >
                          {user.name}
                        </Link>
                      </td>
                      {tags.map(tag => (
                        <td key={`${user.id}-${tag.id}`} className="text-center">
                          {renderCount(
                            getUserTagCount(user, tag.id),
                            tagCountQuery(user, tag)
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={Math.max(tags.length + 1, 2)}
                      className="text-center"
                    >
                      {loader ? "" : "No data found"}
                    </td>
                  </tr>
                )}
                {users.length ? (
                  <tr className="custom-report-total">
                    <td className="text-start">Total</td>
                    {tagTotals.map((total, index) => (
                      <td
                        key={`total-${tags[index]?.id || index}`}
                        className="text-center"
                      >
                        {total}
                      </td>
                    ))}
                  </tr>
                ) : (
                  ""
                )}
              </tbody>
            </table>
          </div>
        </Col>
        <Col lg={4} md={12}>
          <h6 className="text-muted text-uppercase mb-2" style={{ fontSize: "12px" }}>
            Source Wise
          </h6>
          <div className="custom-report-scroll table-responsive">
            <table className="table table-centered table-nowrap custom-report-table">
              <thead>
                <tr>
                  <th className="text-start">Name</th>
                  <th className="text-center">#</th>
                </tr>
              </thead>
              <tbody>
                {visibleSources.length ? (
                  visibleSources.map(source => (
                    <tr key={source.id}>
                      <td className="text-start">{source.name}</td>
                      <td className="text-center">
                        {renderCount(
                          source.count,
                          `/leads${leadListQuery({
                            lead_source_id: source.id,
                          })}`
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2" className="text-center">
                      {loader ? "" : "No data found"}
                    </td>
                  </tr>
                )}
                {visibleSources.length ? (
                  <tr className="custom-report-total">
                    <td className="text-start">Total</td>
                    <td className="text-center">{sourceTotal}</td>
                  </tr>
                ) : (
                  ""
                )}
              </tbody>
            </table>
          </div>
        </Col>
      </Row>
      {error ? (
        <span className="text-danger mt-3 d-table" role="alert">
          {error}
        </span>
      ) : (
        ""
      )}
      {loader}
    </>
  )
})

export default CustomReport

